import { inject, Injectable, signal } from '@angular/core';
import { GeoApifyService } from '../geoapify.service';
import { UntypedFormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, tap } from 'rxjs';
import { UserService } from './user.service';
import { EnvironmentService } from '../environment.service';

@Injectable({
  providedIn: 'root',
})
export class UserSuggestionService {

  public citiesSuggestion = signal<any[]>([]);
  public addressesSuggestion = signal<any[]>([]);
  public zipCodeSuggestion = signal<any[]>([]);

  private _geoApify = inject(GeoApifyService);
  private _user = inject(UserService);
  private _env = inject(EnvironmentService);

  /**
   * Init handle input autocomplete
   *
   * @private
   */
  public initAutocomplete(form: UntypedFormGroup, selectedCountry?: string) {
    const country = this._user?.info?.country?.toLowerCase()
      || form?.get('country')?.value?.toLowerCase()
      || this._env?.env?.country?.short?.toLowerCase()
      || selectedCountry
      || '';

    form?.get('city')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(value => value && value.length >= 3),
      switchMap((value: string) =>
        this._geoApify.getPredictions({
          type: 'city',
          text: value,
          filter: `countrycode:${country}`,
        }),
      ),
      map(res => this._geoApify.filterCities(res)),
      tap(suggestions => this.citiesSuggestion.set(suggestions)),
    ).subscribe();

    form?.get('address')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter(value => value && value.length >= 3),
      switchMap(value =>
        this._geoApify.getPredictions({
          type: 'street',
          text: value,
          filter: `countrycode:${country}`,
        }),
      ),
      map(res => this._geoApify.filterAddress(res)),
      tap(suggestions => this.addressesSuggestion.set(suggestions)),
    ).subscribe();

    const zipCodeControl = form?.get('postal_code');
    if (zipCodeControl) {
      zipCodeControl.valueChanges.pipe(
        startWith(''),
        pairwise(),
        debounceTime(500),
        distinctUntilChanged(),
        filter(([prev, curr]) => curr && Math.abs(curr.length - prev.length) < 2),
        switchMap(([, curr]) =>
          this._geoApify.getPredictions({
            type: 'postcode',
            text: curr,
            filter: `countrycode:${country}`,
          }),
        ),
        map(res => this._geoApify.filterAddress(res)),
        tap(suggestions => this.zipCodeSuggestion.set(suggestions)),
      ).subscribe();
    }
  }

  /**
   * Select suggested value
   *
   * @param suggestion
   * @param input
   */
  public selectSuggestion(form: UntypedFormGroup, suggestion, input: string) {
    switch (input) {
      case 'city':
        if (form?.get('address')?.value) {
          form.get('address').setValue(null);
          form.get('address').markAsUntouched();
        }
        form.get('city')?.setValue(suggestion.city, { emitEvent: false });
        form.get('postal_code')?.setValue(suggestion.postcode, { emitEvent: false });
        break;
      case 'address':
        form.get('address')?.setValue(suggestion.street, { emitEvent: false });
        if (suggestion.postcode) {
          form.get('postal_code')?.setValue(suggestion.postcode, { emitEvent: true });
        }
        break;
      case 'postal_code':
        form.get('postal_code')?.setValue(suggestion.postcode, { emitEvent: false });
        break;
    }
  }
}
